import React, { useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { useLanguage } from '@/contexts/LanguageContext';

const DemoVideo = () => {
  const { t } = useTranslation();
  const { direction } = useLanguage();
  const [activeImage, setActiveImage] = useState<string | null>(null);
  
  const closeModal = () => setActiveImage(null);

  // Handle image click to show in modal
  const handleImageClick = (imageSrc: string) => {
    setActiveImage(imageSrc);
  };

  // List of display examples with descriptions
  const displayExamples = [
    {
      id: 'daily-studies',
      title: direction === 'rtl' ? 'לימודים יומיים' : 'Daily Jewish Studies',
      description: direction === 'rtl' 
        ? 'תצוגת לימודים יומיים כולל תנ״ך, תהילים, חומש, ורמב״ם' 
        : 'Display showing daily Torah studies including Tanach, Tehillim, Chumash, and Rambam readings',
      image: '/images/daily-studies-display.png',
    },
    {
      id: 'prayer-times',
      title: direction === 'rtl' ? 'זמני תפילה ואירועים' : 'Prayer Times & Events',
      description: direction === 'rtl'
        ? 'לוח זמנים לתפילות ואירועים קהילתיים, כולל זמני שבת'
        : 'Schedule showing prayer times and community events, including Shabbat services',
      image: '/images/prayer-times-display.png',
    },
    {
      id: 'admin-dashboard',
      title: direction === 'rtl' ? 'ממשק ניהול' : 'Admin Dashboard',
      description: direction === 'rtl'
        ? 'פאנל ניהול מאפשר לעדכן תצוגות, לקבוע זמנים, ולהתאים תוכן'
        : 'Management interface for updating displays, setting schedules, and customizing content',
      image: '/images/admin-dashboard.png',
    },
  ];

  return (
    <section id="demo" className="py-20 bg-white">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-synagogue-blue">
            {t('demoTitle')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('demoDescription')}
          </p>
        </div>
        
        {/* Featured large preview */}
        <div className="max-w-6xl mx-auto mb-16">
          <div className="bg-gradient-to-br from-synagogue-blue/10 to-synagogue-gold/10 p-8 rounded-2xl shadow-xl">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-synagogue-blue">
                {direction === 'rtl' ? 'מערכת תצוגה דיגיטלית לבתי כנסת' : 'Digital Synagogue Display System'}
              </h3>
              <p className="text-lg text-gray-600 mt-2">
                {direction === 'rtl' 
                  ? 'הצגת מידע חשוב בצורה מהודרת ועדכנית' 
                  : 'Presenting important information in an elegant and up-to-date manner'}
              </p>
            </div>
            
            <div className="aspect-w-16 aspect-h-9 bg-synagogue-light rounded-xl overflow-hidden shadow-xl">
              <img 
                src="/images/daily-studies-display.png"
                alt="Digital Synagogue Board - Featured Display"
                className="w-full h-full object-contain cursor-pointer"
                onClick={() => handleImageClick('/images/daily-studies-display.png')}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = 'https://placehold.co/1200x700/FDF8E3/1C2B5E?text=Add+daily-studies-display.png+to+public/images';
                }}
              />
            </div>
          </div>
        </div>
        
        {/* Display examples with descriptions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {displayExamples.map((example) => (
            <div key={example.id} className="bg-white rounded-xl shadow-lg overflow-hidden border border-synagogue-blue/10 hover:border-synagogue-gold/50 transition-all duration-300 hover:shadow-xl">
              <div className="p-4 bg-synagogue-blue text-white text-center font-bold">
                {example.title}
              </div>
              <div className="p-5">
                <div 
                  className="aspect-w-4 aspect-h-3 mb-4 cursor-pointer"
                  onClick={() => handleImageClick(example.image)}
                >
                  <img
                    src={example.image}
                    alt={example.title}
                    className="w-full h-auto rounded-lg object-cover hover:opacity-90 transition-opacity"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = `https://placehold.co/400x300/FDF8E3/1C2B5E?text=Add+${example.id}.png+to+public/images`;
                    }}
                  />
                </div>
                <p className="text-gray-700">{example.description}</p>
                
                <div className="mt-4 text-center">
                  <button
                    onClick={() => handleImageClick(example.image)}
                    className="text-synagogue-blue hover:text-synagogue-gold font-medium inline-flex items-center transition-colors"
                  >
                    <span>{direction === 'rtl' ? 'הגדל תצוגה' : 'View Larger'}</span>
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Image modal */}
        {activeImage && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={closeModal}>
            <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
              <div className="relative">
                <img 
                  src={activeImage} 
                  alt="Enlarged view" 
                  className="w-full h-auto max-h-[80vh] object-contain bg-white p-2 rounded-lg"
                />
                <button 
                  className="absolute top-3 right-3 bg-synagogue-blue hover:bg-synagogue-gold text-white p-2 rounded-full transition-colors shadow-lg"
                  onClick={closeModal}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default DemoVideo; 